import React, { useState } from 'react';
import { ArrowRight, FileText, Database, Palette, Code, Server, CheckCircle2 } from 'lucide-react';
import Reveal from './Reveal';

const icons = { FileText, Database, Palette, Code, Server, CheckCircle2 };

const ROLE_DATA = [
    {
        id: 'ba', role: 'Business Analysts', subtext: 'From conversation to documentation', icon: 'FileText', color: 'from-cyan-500 to-blue-500', iconBg: 'bg-cyan-500/10', iconColor: 'text-cyan-400', borderColor: 'border-cyan-500/20',
        benefits: ['Chat naturally to create PRDs and BRDs', 'AI-structured documentation from your conversations', 'Convert requirements into API specifications', 'Reusable document templates'],
        workflow: ['Start a conversation', 'AI creates structured document', 'Review and refine', 'Convert to API specs', 'Continue anytime']
    },
    {
        id: 'db', role: 'Database Engineers', subtext: 'Smart database design from requirements', icon: 'Database', color: 'from-indigo-500 to-blue-500', iconBg: 'bg-indigo-500/10', iconColor: 'text-indigo-400', borderColor: 'border-indigo-500/20',
        benefits: ['Notified when a project needs database work', 'AI-generated schemas from requirements', 'Chat-based schema refinement', 'Best-practice guidance built in'],
        workflow: ['Receive notification', 'Review requirements', 'AI suggests schemas', 'Refine through chat', 'Export design']
    },
    {
        id: 'designer', role: 'Designers', subtext: 'Design with context and clarity', icon: 'Palette', color: 'from-fuchsia-500 to-pink-500', iconBg: 'bg-fuchsia-500/10', iconColor: 'text-fuchsia-400', borderColor: 'border-fuchsia-500/20',
        benefits: ['Auto-generated user stories from requirements', 'Full context from business requirements', 'Streamlined design-to-development handoff', 'Clear specifications for developers'],
        workflow: ['Access user stories', 'Design screens', 'Hand off to developers', 'Collaborate on specifications']
    },
    {
        id: 'frontend', role: 'Frontend Developers', subtext: 'Code without waiting for backend', icon: 'Code', color: 'from-rose-500 to-red-500', iconBg: 'bg-rose-500/10', iconColor: 'text-rose-400', borderColor: 'border-rose-500/20',
        benefits: ['Start coding immediately against mock APIs', 'Realistic endpoints with recorded or authored responses', 'Simply swap URLs when the backend is ready', 'Test end-to-end before backend completion'],
        workflow: ['Receive design notification', 'Access mock APIs', 'Code and test', 'Replace with real APIs']
    },
    {
        id: 'backend', role: 'Backend Engineers', subtext: 'Build with clarity and best practices', icon: 'Server', color: 'from-orange-500 to-amber-500', iconBg: 'bg-orange-500/10', iconColor: 'text-orange-400', borderColor: 'border-orange-500/20',
        benefits: ['Start with complete API contracts', 'Reference working mock implementations', 'Database schemas already designed', 'Best-practice and security guidance', 'Frontend perspective built-in'],
        workflow: ['Review contracts and schemas', 'Use mocks as reference', 'Implement with guidance', 'Deploy with confidence']
    },
    {
        id: 'tester', role: 'Testers', subtext: 'Test earlier, find bugs faster', icon: 'CheckCircle2', color: 'from-emerald-500 to-green-500', iconBg: 'bg-emerald-500/10', iconColor: 'text-emerald-400', borderColor: 'border-emerald-500/20',
        benefits: ['Complete test cases auto-generated', 'Mock APIs with multiple test scenarios', 'Track test runs and results', 'Start testing before the backend is done'],
        workflow: ['Access test cases', 'Test with mocks', 'Report edge cases', 'Comprehensive coverage']
    }
];

const ShiftLeft = () => {
    const [selectedRole, setSelectedRole] = useState(ROLE_DATA[0]);
    const [isVisible, setIsVisible] = useState(true);

    const handleRoleChange = (role) => {
        if (role.id === selectedRole.id) return;
        setIsVisible(false);
        setTimeout(() => {
            setSelectedRole(role);
            setIsVisible(true);
        }, 150);
    };

    return (
        <section>
            {/* Shift Left Banner */}
            <div className="relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 via-purple-500/10 to-cyan-500/10 pointer-events-none" />
                <div className="border-y border-white/5 py-12 sm:py-16">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                      <Reveal>
                        <div className="bg-[#111111] rounded-2xl sm:rounded-3xl p-8 sm:p-10 md:p-14 border border-white/5 flex flex-col md:flex-row items-center justify-between gap-8 sm:gap-10 text-center md:text-left relative overflow-hidden">
                            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 via-transparent to-purple-500/5 pointer-events-none" />
                            <div className="max-w-2xl relative z-10">
                                <h3 className="text-white text-2xl sm:text-3xl font-extrabold mb-4 tracking-tight font-mono">
                                    <span className="text-cyan-400">{'>'}</span> Shift Left, Ship Faster
                                </h3>
                                <p className="text-gray-400 text-base sm:text-lg leading-relaxed">Move tasks earlier in the development cycle. Frontend developers code against mocks, testers start testing sooner, and teams deliver faster.</p>
                            </div>
                            <button className="relative z-10 group bg-gradient-to-r from-cyan-400 to-cyan-300 text-black px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-mono font-bold text-sm sm:text-base hover:from-cyan-300 hover:to-cyan-200 transition-all flex items-center gap-3 shadow-lg shadow-cyan-500/20 hover:shadow-cyan-500/40 flex-shrink-0">
                                <span className="text-cyan-700 group-hover:translate-x-0.5 transition-transform">{'>'}</span>
                                Learn More
                                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </button>
                        </div>
                      </Reveal>
                    </div>
                </div>
            </div>

            {/* Team Roles Section */}
            <div className="py-20 sm:py-28" id="teams">
                <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 text-center">
                    <Reveal>
                        <h2 className="text-2xl sm:text-4xl md:text-5xl font-extrabold text-white tracking-tight mb-4 sm:mb-8 px-1">
                            Empowering Your{' '}
                            <span className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">Entire Team</span>
                        </h2>
                        <p className="max-w-3xl mx-auto text-sm sm:text-lg text-gray-500 mb-10 sm:mb-16 leading-relaxed px-1">
                            Every team member gets AI-powered tools tailored to their role, creating a seamless workflow from requirements to deployment.
                        </p>
                    </Reveal>

                    {/* Role selector grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 sm:gap-4 text-left mb-8 sm:mb-12">
                        {ROLE_DATA.map((item) => {
                            const Icon = icons[item.icon];
                            const isSelected = selectedRole.id === item.id;
                            return (
                                <button
                                    key={item.id}
                                    onClick={() => handleRoleChange(item)}
                                    className={`p-3.5 sm:p-6 rounded-xl border text-left transition-all duration-300 group ${
                                        isSelected
                                            ? `border-cyan-500/30 bg-cyan-500/5 shadow-lg shadow-cyan-500/5`
                                            : 'border-white/5 hover:border-white/10 bg-[#111111]'
                                    }`}
                                >
                                    <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg ${item.iconBg} border ${item.borderColor} flex items-center justify-center ${item.iconColor} mb-2.5 sm:mb-4 transition-transform group-hover:scale-110`}>
                                        <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                                    </div>
                                    <h4 className={`text-xs sm:text-base font-bold mb-0.5 sm:mb-1 leading-tight font-mono ${isSelected ? 'text-cyan-400' : 'text-white'}`}>{item.role}</h4>
                                    <p className="text-gray-600 text-label sm:text-sm leading-snug">{item.subtext}</p>
                                </button>
                            );
                        })}
                    </div>

                    {/* Selected role detail card */}
                    <div className={`bg-[#111111] rounded-xl sm:rounded-2xl border border-white/5 relative overflow-hidden text-left transition-all duration-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                        {/* File tab header */}
                        <div className="flex items-center gap-2 px-4 py-3 bg-[#0d0d0d] border-b border-white/5">
                            <div className="flex gap-1.5">
                                <div className="w-3 h-3 rounded-full bg-[#ff5f57]" />
                                <div className="w-3 h-3 rounded-full bg-[#febc2e]" />
                                <div className="w-3 h-3 rounded-full bg-[#28c840]" />
                            </div>
                            <span className="font-mono text-xs text-gray-600 ml-2">{selectedRole.id}-workflow.ts</span>
                        </div>

                        <div className="p-5 sm:p-8 md:p-12">
                            <div className="flex items-center gap-3 sm:gap-5 mb-6 sm:mb-10">
                                <div className={`w-10 h-10 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl ${selectedRole.iconBg} border ${selectedRole.borderColor} flex items-center justify-center ${selectedRole.iconColor} flex-shrink-0`}>
                                    {React.createElement(icons[selectedRole.icon], { className: "w-5 h-5 sm:w-7 sm:h-7" })}
                                </div>
                                <div>
                                    <h3 className="text-base sm:text-2xl font-extrabold text-white mb-0.5 sm:mb-1 font-mono">{selectedRole.role}</h3>
                                    <p className="text-gray-500 text-xs sm:text-base">{selectedRole.subtext}</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-8">
                                <div>
                                    <h4 className="text-label sm:text-xs font-bold text-gray-600 mb-3 sm:mb-6 uppercase tracking-[0.2em] font-mono">Key Benefits</h4>
                                    <div className="grid grid-cols-1 gap-y-2.5 sm:gap-y-4">
                                        {selectedRole.benefits.map((benefit, i) => (
                                            <div key={i} className="flex items-start gap-2.5 sm:gap-3">
                                                <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-md bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 flex-shrink-0 mt-0.5">
                                                    <CheckCircle2 className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                                                </div>
                                                <p className="text-gray-400 text-xs sm:text-sm leading-snug">{benefit}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="bg-[#0a0a0a] rounded-xl p-4 sm:p-6 border border-white/5">
                                    <h4 className="text-label sm:text-xs font-bold text-gray-600 mb-3 sm:mb-6 uppercase tracking-[0.2em] font-mono">Typical Workflow</h4>
                                    <div className="flex flex-wrap items-center gap-x-2 sm:gap-x-3 gap-y-2 sm:gap-y-3 text-gray-400 text-xs sm:text-sm font-mono leading-relaxed">
                                        {selectedRole.workflow.map((step, i) => {
                                            const isLast = i === selectedRole.workflow.length - 1;
                                            return (
                                                <React.Fragment key={i}>
                                                    <span className="text-gray-300 bg-white/5 px-2 py-0.5 rounded">{step}</span>
                                                    {!isLast && <span className="text-cyan-500/40">→</span>}
                                                </React.Fragment>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ShiftLeft;
